import { logAuthError } from '@/components/auth/log-auth-error';
import { supabase } from '@/integrations/supabase/client';
import { isLockContentionError } from '@/lib/auth-utils';

import { mapPathForNative } from './auth-callback-routing';

const getRedirectPathForRole = (role: string | null): string => {
  const isAdminRole = ['admin', 'staff', 'assistant'].includes(role || '');
  return isAdminRole ? '/admin' : '/dashboard';
};

type LookupOperation = 'role' | 'membership' | 'invitation';

/**
 * One routing lookup's answer. `failed` is deliberately its own outcome: a
 * lookup that could not answer - a returned error, a throw, a payload of the
 * wrong shape - has said nothing about access, and must never be read as "no".
 */
type Lookup<T> =
  | { readonly outcome: 'found'; readonly value: T }
  | { readonly outcome: 'absent' }
  | { readonly outcome: 'failed' };

export type RoutingDecision =
  | { readonly kind: 'admit'; readonly path: string }
  | { readonly kind: 'deny' }
  | { readonly kind: 'undecided' };

const ABSENT = { outcome: 'absent' } as const;
const FAILED = { outcome: 'failed' } as const;

const WELCOME_PATH = '/welcome';

/**
 * A successful response whose body is not the shape the query asks for. The
 * client types are compile-time only, so this is checked at runtime and treated
 * as a failure: an unrecognised answer is not a "no".
 */
const UNEXPECTED_PAYLOAD = { name: 'UnexpectedPayload' } as const;

/**
 * Identifying fields only. A PostgrestError's message, details and hint can
 * echo row values, so they never reach telemetry; `status` comes from the
 * response because the error object does not carry it, and `0` is what
 * postgrest-js reports for a failure raised on the client side.
 */
const reportLookupFailure = (operation: LookupOperation, error: unknown, status?: number): void => {
  if (isLockContentionError(error)) {
    return;
  }
  const { name, code } = (error ?? {}) as { name?: unknown; code?: unknown };
  logAuthError(`OAuth callback lookup failed: ${operation}`, { name, code, status });
};

const lookUpRole = async (userId: string): Promise<Lookup<string | null>> => {
  try {
    // maybeSingle, not single: zero rows is an answer ("no platform role"),
    // not an error. More than one row cannot happen - UNIQUE (user_id).
    const { data, error, status } = await supabase
      .from('user_role_assignments')
      .select('role')
      .eq('user_id', userId)
      .maybeSingle()
      .overrideTypes<{ role: string | null } | null>();
    if (error) {
      reportLookupFailure('role', error, status);
      return FAILED;
    }
    if (data === null) {
      return ABSENT;
    }
    const role: unknown =
      typeof data === 'object' && !Array.isArray(data)
        ? (data as { role?: unknown }).role
        : undefined;
    if (role === null) {
      return { outcome: 'found', value: null };
    }
    if (typeof role === 'string') {
      return { outcome: 'found', value: role };
    }
    reportLookupFailure('role', UNEXPECTED_PAYLOAD, status);
    return FAILED;
  } catch (error) {
    reportLookupFailure('role', error);
    return FAILED;
  }
};

const lookUpMembership = async (userId: string): Promise<Lookup<true>> => {
  try {
    const { data, error, status } = await supabase
      .from('organization_members')
      .select('id')
      .eq('user_id', userId)
      .eq('status', 'active')
      .limit(1);
    if (error) {
      reportLookupFailure('membership', error, status);
      return FAILED;
    }
    const rows: unknown = data;
    if (!Array.isArray(rows)) {
      reportLookupFailure('membership', UNEXPECTED_PAYLOAD, status);
      return FAILED;
    }
    if (rows.length === 0) {
      return ABSENT;
    }
    const first: unknown = rows[0];
    const id: unknown =
      typeof first === 'object' && first !== null ? (first as { id?: unknown }).id : undefined;
    if (typeof id === 'string' && id !== '') {
      return { outcome: 'found', value: true };
    }
    reportLookupFailure('membership', UNEXPECTED_PAYLOAD, status);
    return FAILED;
  } catch (error) {
    reportLookupFailure('membership', error);
    return FAILED;
  }
};

const lookUpInvitation = async (): Promise<Lookup<true>> => {
  try {
    // The RPC derives the email from the caller's JWT (auth.email()); it takes
    // no argument, and answers with an explicit boolean on every success path.
    const { data, error, status } = await supabase.rpc('validate_oauth_signup');
    if (error) {
      reportLookupFailure('invitation', error, status);
      return FAILED;
    }
    const hasInvitation: unknown =
      typeof data === 'object' && data !== null && !Array.isArray(data)
        ? (data as { has_invitation?: unknown }).has_invitation
        : undefined;
    if (hasInvitation === true) {
      return { outcome: 'found', value: true };
    }
    if (hasInvitation === false) {
      return ABSENT;
    }
    reportLookupFailure('invitation', UNEXPECTED_PAYLOAD, status);
    return FAILED;
  } catch (error) {
    reportLookupFailure('invitation', error);
    return FAILED;
  }
};

/**
 * Where an invited user lands. The router only defines /welcome, and the
 * welcome page reads the token from the query string - a path segment lands on
 * NotFound. Stored data that is not a usable token still reaches /welcome,
 * which can resolve the invitation from the signed-in email.
 */
const readWelcomePath = (): string => {
  const stored = sessionStorage.getItem('invitation_data');
  if (!stored) {
    return WELCOME_PATH;
  }
  try {
    const parsed: unknown = JSON.parse(stored);
    const token: unknown =
      typeof parsed === 'object' && parsed !== null
        ? (parsed as { token?: unknown }).token
        : undefined;
    return typeof token === 'string' && token !== ''
      ? `${WELCOME_PATH}?token=${encodeURIComponent(token)}`
      : WELCOME_PATH;
  } catch {
    return WELCOME_PATH;
  }
};

/**
 * Admit on any positive answer; deny only on three authoritative negatives.
 * Anything in between is undecided - denying there signs a member out on a
 * lookup that merely failed to answer.
 */
export const decideRouting = async (userId: string): Promise<RoutingDecision> => {
  const role = await lookUpRole(userId);
  if (role.outcome === 'found') {
    return { kind: 'admit', path: mapPathForNative(getRedirectPathForRole(role.value)) };
  }
  const membership = await lookUpMembership(userId);
  if (membership.outcome === 'found') {
    return { kind: 'admit', path: mapPathForNative('/') };
  }
  const invitation = await lookUpInvitation();
  if (invitation.outcome === 'found') {
    return { kind: 'admit', path: readWelcomePath() };
  }
  if (
    role.outcome === 'absent' &&
    membership.outcome === 'absent' &&
    invitation.outcome === 'absent'
  ) {
    return { kind: 'deny' };
  }
  return { kind: 'undecided' };
};
